type Parent = {
  actionPkInitRoad?: string;
  pkInitRoad?: string;
  actionPkEndRoad?: string;
  pkEndRoad?: string;
};

export const pkValidation = (value: string, parent: Parent) => {
  const val = value.split('+');
  const initValue = (parent?.actionPkInitRoad?.split('+') ??
    parent?.pkInitRoad?.split('+')) as string[];
  const endValue = (parent?.actionPkEndRoad?.split('+') ??
    parent?.pkEndRoad?.split('+')) as string[];
  const maxKilometer = Math.max(Number(initValue[0]), Number(endValue[0]));
  const minKilometer = Math.min(Number(initValue[0]), Number(endValue[0]));
  const maxMeter =
    maxKilometer === Number(initValue[0])
      ? Number(initValue[1])
      : Number(endValue[1]);

  const minMeter =
    minKilometer === Number(initValue[0])
      ? Number(initValue[1])
      : Number(endValue[1]);

  if (Number(val[0]) === maxKilometer) {
    return Number(val[val.length - 1]) <= maxMeter;
  } else if (Number(val[0]) === minKilometer) {
    return Number(val[val.length - 1]) >= minMeter;
  } else {
    return Number(val[0]) > minKilometer && Number(val[0]) < maxKilometer;
  }
};

/** Extremos de una carretera, tal y como los sirve la caché de datos maestros. */
export type RoadPkRange = {
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
};

/** Pinta los extremos en el formato de cadena que espera `pkValidation`. */
export const roadPkRangeAFormulario = (
  road?: RoadPkRange | null
): { pkInitRoad: string; pkEndRoad: string } =>
  road
    ? {
        pkInitRoad: `${road.pkInitKm}+${road.pkInitMeter}`,
        pkEndRoad: `${road.pkEndKm}+${road.pkEndMeter}`,
      }
    : { pkInitRoad: '', pkEndRoad: '' };

/**
 * Test de yup «el PK cae dentro de la carretera», para usar dentro de un
 * `.test()` del schema — que es donde vive esta validación en los otros cinco
 * formularios que ya la hacen (NewRoute, NewWorkOrderForm, NewCommunicationForm,
 * LocationForm, ShowOT/WorkOrder).
 *
 * 🔴 **Fail-open cuando no hay extremos** (`pkInitRoad`/`pkEndRoad` vacíos: la
 * carretera aún no ha llegado de la caché, o el recorrido no tiene ninguna).
 * Sin este guard el fail-open se convertiría en fail-CLOSED silencioso:
 * `''.split('+')` da `['']` y `Number('')` es 0, así que `pkValidation`
 * compararía contra el rango 0→0 y rechazaría **todo**. Un Tablet sin cobertura
 * tiene que poder abrir y cerrar tramos; el backend es la autoridad.
 */
export const pkDentroDeLaCarretera = (
  value: string | undefined,
  parent: Parent
): boolean => {
  if (!parent?.pkInitRoad || !parent?.pkEndRoad) return true;
  return pkValidation(value ?? '', parent);
};

/**
 * Umbral (en metros) a partir del cual una OT se considera «larga» (una campaña
 * o una genérica de decenas de km): su rango de PK ya no describe dónde está un
 * trabajo concreto.
 */
export const LONG_ORDER_SPAN_METERS = 4000;

/**
 * Metros aproximados entre el PK inicial y el final de una OT
 * (|Δkm·1000 + Δm|). Es aproximado a propósito: con un kilómetro largo
 * (p. ej. el 84 de la C-17, 1.890 m) no es una distancia exacta, pero sirve para
 * decidir «puntual o larga». Nunca se usa para ordenar ni validar posiciones.
 */
export const orderSpanMeters = (o: {
  pkInitKm: number;
  pkInitMeter: number;
  pkEndKm: number;
  pkEndMeter: number;
}): number =>
  Math.abs((o.pkEndKm - o.pkInitKm) * 1000 + (o.pkEndMeter - o.pkInitMeter));
